// ProgressMeter — "how much of this is done", as a bar a screen reader can read
// (story 5-10b; P05 §9.2: the capture header shows "required completion").
//
// WHY A PRIMITIVE AND NOT A DIV IN THE PAGE. Three surfaces in this wave want the
// same shape — capture's required-completion header, 5-10e's submit sheet, and
// 5-3's workload board column fill — and a bar is exactly the kind of thing every
// page re-invents slightly differently until one of them ships an unlabelled
// `<div style={{width}}>` that announces nothing at all. So the a11y contract
// lives here, once: `role="progressbar"` with min/max/now AND `aria-valuetext`,
// because "7" read alone is meaningless — the useful announcement is
// "7 of 12 required questions answered".
//
// WHY NOT `<progress>`. The native element cannot be styled to the glass system
// across browsers without `appearance:none` plus a per-engine pseudo-element
// stack, and it drops the accessible name in several screen-reader/browser
// pairs. A div with the explicit ARIA contract is the honest trade.
//
// COLOUR is semantic tokens only (`bg-primary`, `bg-success`, `bg-muted`) — the
// track/fill pair is checked by `frontend/scripts/contrast-check.mjs` through the
// text roles it labels, and no literal hex appears here (DOD §1.7).

// Story 2-147qb: `tone` — additive, default-preserving. `UsageMeter.tsx`
// (components/billing, dead-consumer debt found and converged by this
// story) needed a THIRD fill colour a plain complete/incomplete read can't
// express: a quota gauge where APPROACHING the total is bad (warn, then
// destructive), the inverse of a task-completion bar where reaching the
// total is good. `auto` keeps every existing consumer's rendered colour
// byte-identical (it IS the old `complete ? success : primary` logic); an
// explicit tone lets a quota-shaped caller override it.
export type ProgressMeterTone = 'auto' | 'primary' | 'success' | 'warning' | 'destructive';

const TONE_CLASS: Record<Exclude<ProgressMeterTone, 'auto'>, string> = {
  primary: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  destructive: 'bg-destructive',
};

export interface ProgressMeterProps {
  /** How many units are done. Clamped into [0, total]. */
  value: number;
  /** How many units there are in total. `0` renders the "nothing to do" state. */
  total: number;
  /**
   * The accessible name — REQUIRED, not optional. A bar with no name is a
   * decoration, and a decoration should not be a progressbar.
   */
  label: string;
  /**
   * The spoken value. Defaults to "<value> of <total> <label>", which is right
   * for nearly every case; pass one when the units need naming differently.
   */
  valueText?: string;
  /** Show the "7 / 12" counter beside the bar. */
  showCount?: boolean;
  /** `sm` for a header strip, `md` for a standalone bar. */
  size?: 'sm' | 'md';
  /** `auto` (default): primary while incomplete, success at 100% — the task-completion reading every pre-2-147qb caller already gets. Pass an explicit tone when the caller's own semantics invert that (e.g. a quota gauge, where the total is a cap, not a goal). */
  tone?: ProgressMeterTone;
  className?: string;
}

export function ProgressMeter({
  value,
  total,
  label,
  valueText,
  showCount = false,
  size = 'md',
  tone = 'auto',
  className,
}: ProgressMeterProps): JSX.Element {
  const safeTotal = Number.isFinite(total) && total > 0 ? Math.floor(total) : 0;
  const safeValue = Math.min(Math.max(Number.isFinite(value) ? Math.floor(value) : 0, 0), safeTotal);
  // A form with no required questions is COMPLETE, not 0% — "0 of 0" drawn as an
  // empty bar reads as "you have work to do" and there is none.
  const complete = safeValue >= safeTotal;
  const percent = safeTotal === 0 ? 100 : Math.round((safeValue / safeTotal) * 100);
  const fillClass = tone === 'auto' ? (complete ? 'bg-success' : 'bg-primary') : TONE_CLASS[tone];

  return (
    <div className={`flex items-center gap-2 ${className ?? ''}`}>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={safeTotal}
        aria-valuenow={safeValue}
        aria-valuetext={valueText ?? `${safeValue} of ${safeTotal} ${label}`}
        className={`relative w-full overflow-hidden rounded-full bg-muted ${size === 'sm' ? 'h-1.5' : 'h-2.5'}`}
      >
        <div
          className={`h-full rounded-full transition-[width] duration-300 ${fillClass}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      {showCount && (
        <span className="shrink-0 text-xs font-semibold tabular-nums text-muted-foreground" aria-hidden>
          {safeValue}/{safeTotal}
        </span>
      )}
    </div>
  );
}
